import { put, select, takeLatest } from 'redux-saga/effects';
import { reportFilterSelector, reportOpStructuresFilterSelector } from '..';
import { TableExportPdfAdapter, TablesExportPdf } from '../../exports';
import { OpStructuresFilter, ReportFilter } from '../../types';
import { enqueueNotification } from '../notifications/actionCreators';
import { downloadSuccess, downloadFailure, downloadRequested } from './actionCreators';
import { ActionType, DownloadReportPdfAction, DownloadRequestedAction } from './types';

const getParamtersTable = ({
  t,
  title,
  filters,
  opsFilters,
  locale,
  parameters = [],
  opStructures = [],
}: DownloadReportPdfAction & {
  filters: ReportFilter;
  opsFilters: OpStructuresFilter;
}) => {
  const values = {
    header: t('dataGridFlexSales.exports.labels.parameterValue'),
    countryCode: t(`operativeStructure.countries.${filters.countryCode}`),
    stores: filters.stores.map(i => i.storeAcronym).join(', '),
    pos: filters.pos.join(', '),
    segments: filters.segments.map(i => i.segmentDesc).join(', '),
    startAccountingDate: filters.startAccountingDate.accept,
    endAccountingDate: filters.endAccountingDate.accept,
    startOperationDate: filters.startOperationDate.accept,
    endOperationDate: filters.endOperationDate.accept,
    startBusinessDate: filters.startBusinessDate.accept,
    endBusinessDate: filters.endBusinessDate.accept,
    regionalMgmt: opsFilters.regionalMgmt.map(i => i.regionalMgmt).join(', '),
    region: opsFilters.region.map(i => i.region).join(', '),
    management: opsFilters.management.map(i => i.mgmt).join(', '),
    supervision: opsFilters.supervision.map(i => i.supervision).join(', '),
    agreement: opsFilters.agreement.map(i => i.agreementType).join(', '),
    company: opsFilters.company.map(i => i.companyName).join(', '),
  };

  const adapter = new TableExportPdfAdapter<typeof values>([values], locale);

  adapter.addColumn({
    type: 'string',
    value: 'header',
    title: t('dataGridFlexSales.exports.labels.parameter'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'countryCode',
    title: t('toolbar.labels.country'),
    available: () => parameters.includes('countryCode'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'stores',
    title: t('toolbar.labels.store'),
    available: () => parameters.includes('stores'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'pos',
    title: t('toolbar.labels.pos'),
    available: () => parameters.includes('pos'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'segments',
    title: t('toolbar.labels.pos-type'),
    available: () => parameters.includes('segments'),
  });
  adapter.addColumn({
    type: 'date',
    value: 'startAccountingDate',
    title: t('toolbar.labels.fromDateAccounting'),
    available: () => parameters.includes('startAccountingDate'),
  });
  adapter.addColumn({
    type: 'date',
    value: 'endAccountingDate',
    title: t('toolbar.labels.toDateAccounting'),
    available: () => parameters.includes('endAccountingDate'),
  });
  adapter.addColumn({
    type: 'date',
    value: 'startOperationDate',
    title: t('toolbar.labels.fromDateOperation'),
    available: () => parameters.includes('startOperationDate'),
  });
  adapter.addColumn({
    type: 'date',
    value: 'endOperationDate',
    title: t('toolbar.labels.toDateOperation'),
    available: () => parameters.includes('endOperationDate'),
  });
  adapter.addColumn({
    type: 'date',
    value: 'startBusinessDate',
    title: t('toolbar.labels.fromDateBusiness'),
    available: () => parameters.includes('startBusinessDate'),
  });
  adapter.addColumn({
    type: 'date',
    value: 'endBusinessDate',
    title: t('toolbar.labels.toDateBusiness'),
    available: () => parameters.includes('endBusinessDate'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'regionalMgmt',
    title: t('toolbar.labels.regionalMgmt'),
    available: () => opStructures.includes('regionalMgmt'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'region',
    title: t('toolbar.labels.region'),
    available: () => opStructures.includes('region'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'management',
    title: t('toolbar.labels.management'),
    available: () => opStructures.includes('management'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'supervision',
    title: t('toolbar.labels.supervision'),
    available: () => opStructures.includes('supervision'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'agreement',
    title: t('toolbar.labels.agreement'),
    available: () => opStructures.includes('agreement'),
  });
  adapter.addColumn({
    type: 'string',
    value: 'company',
    title: t('toolbar.labels.company'),
    available: () => opStructures.includes('company'),
  });

  return adapter.getTableTranspose({
    title,
    getColumnConfig: headers => headers.map((_, i) => ({ width: i ? 250 : 100, align: 'left' })),
  });
};

function* download({ downloadHandler, errorMessage }: DownloadRequestedAction) {
  try {
    yield downloadHandler();
    yield put(downloadSuccess());
  } catch (error) {
    yield put(downloadFailure({ error }));
    yield put(
      enqueueNotification({
        key: 'downloadError',
        message: errorMessage,
        variant: 'error',
      })
    );
  }
}

function* downloadReportPdfHandler(action: DownloadReportPdfAction) {
  const { title, table, parameters } = action;
  const filters: ReportFilter = yield select(reportFilterSelector);
  const opsFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  const tablesExport = new TablesExportPdf(action.theme);

  if (parameters?.length)
    tablesExport.pushTable(
      'parameters',
      yield getParamtersTable({ filters, opsFilters, ...action })
    );

  tablesExport.pushTable('table', table);
  tablesExport.export(title);
}

function* downloadReportPdf(action: DownloadReportPdfAction) {
  yield put(
    downloadRequested({
      downloadHandler: () => downloadReportPdfHandler(action),
      errorMessage: t => t('errors.exports.body', { type: 'PDF' }),
    })
  );
}

export default [
  takeLatest(ActionType.downloadRequested, download),
  takeLatest(ActionType.downloadReportPdf, downloadReportPdf),
];
